import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { useEscape } from './hooks';
import { phoneAsTyped } from './format';
import type { ApiError } from './api';
import './components.css';

type Tone = 'accent' | 'go' | 'danger' | 'quiet' | 'ghost';

export function Button({
  tone = 'accent',
  small = false,
  wide = false,
  pending = false,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: Tone;
  small?: boolean;
  wide?: boolean;
  pending?: boolean;
}) {
  const tones: Record<Tone, string> = {
    accent: '',
    go: 'k-go',
    danger: 'k-danger',
    quiet: 'k-quiet',
    ghost: 'k-ghost',
  };
  return (
    <button
      type="button"
      className={['k-btn', tones[tone], small ? 'k-small' : '', wide ? 'k-wide' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      disabled={rest.disabled || pending}
      {...rest}
    >
      {pending && <span className={`k-spinner${tone === 'quiet' || tone === 'ghost' ? '' : ' k-on-accent'}`} />}
      {children}
    </button>
  );
}

export function Panel({
  children,
  pad = true,
  className,
}: {
  children: ReactNode;
  pad?: boolean;
  className?: string;
}) {
  return (
    <section className={['k-panel', pad ? 'k-panel-pad' : '', className ?? ''].filter(Boolean).join(' ')}>
      {children}
    </section>
  );
}

export function PanelHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <header className="k-panel-head">
      <h2 className="t-heading">{title}</h2>
      <span className="k-spacer" />
      {children}
    </header>
  );
}

export function Field({
  label,
  hint,
  bad,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  bad?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="k-field">
      <span className="t-label">{label}</span>
      {children}
      {hint !== undefined && <span className={`k-hint${bad ? ' k-bad' : ''}`}>{hint}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="k-input" {...props} />;
}

/**
 * A box that wears «+7 926 341-08-72» as the number is typed into it.
 *
 * The mask goes on in the change handler; the caret is put back afterwards,
 * because writing a new value into a controlled input otherwise drops it at the
 * end and editing the middle of a number becomes impossible. Masking the text in
 * front of the caret gives the prefix of the mask, so wherever those characters
 * land is where the caret belongs.
 *
 * Styled like [TextInput] and overridable the same way: the panel takes the class
 * as it comes, the passenger app passes its own.
 */
export function PhoneInput({
  value,
  onChange,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> & {
  value: string;
  onChange: (phone: string) => void;
}) {
  const box = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const at = caret.current;
    caret.current = null;
    if (at !== null) box.current?.setSelectionRange(at, at);
  });

  return (
    <input
      className="k-input"
      {...rest}
      ref={box}
      type="tel"
      inputMode="tel"
      value={value}
      onChange={(event) => {
        const typed = event.target.value;
        const upTo = event.target.selectionStart ?? typed.length;
        caret.current = phoneAsTyped(typed.slice(0, upTo)).length;
        onChange(phoneAsTyped(typed));
      }}
    />
  );
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="k-textarea" {...props} />;
}

export function Badge({
  tone = 'neutral',
  dot = false,
  children,
}: {
  tone?: 'neutral' | 'go' | 'wait' | 'danger' | 'accent';
  dot?: boolean;
  children: ReactNode;
}) {
  const tones = { neutral: '', go: 'k-go', wait: 'k-wait', danger: 'k-danger', accent: 'k-accent' };
  return (
    <span className={`k-badge ${tones[tone]}`.trim()}>
      {dot && <span className="k-dot" />}
      {children}
    </span>
  );
}

/**
 * What the server said, shown verbatim. Every error the API returns carries a
 * Russian `message` written to be read by the person who caused it, so the UI's
 * job is to display it rather than to translate a status code into a guess.
 */
export function Problem({ error }: { error: ApiError | null | undefined }) {
  if (!error) return null;
  return (
    <div className="k-problem" role="alert">
      <span aria-hidden="true">⚠</span>
      <span>{error.message}</span>
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="k-empty">
      <span className="t-strong">{title}</span>
      {hint && <span className="t-caption">{hint}</span>}
    </div>
  );
}

export function Spinner() {
  return <span className="k-spinner" />;
}

export function Modal({
  title,
  open,
  onClose,
  width = 460,
  className,
  children,
  footer,
}: {
  title: ReactNode;
  open: boolean;
  onClose: () => void;
  width?: number;
  className?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div
      className="k-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={className ? `k-modal ${className}` : 'k-modal'} style={{ maxWidth: width }} role="dialog" aria-modal="true">
        <div className="k-modal-head">
          <h2 className="t-heading">{title}</h2>
          <span className="k-spacer" style={{ flex: 1 }} />
          <button className="k-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="k-modal-body">{children}</div>
        {footer && <div className="k-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** How long the face takes to grow out of its circle, and to shrink back into it. */
const FACE_FLIGHT_MS = 260;
const FACE_RETURN_MS = 220;

/**
 * A face full screen, grown out of the circle at [from] and shrunk back into it on the
 * way out. A tap anywhere, Escape or a drag up or down closes it. A port of `showFace`.
 *
 * Still a circle at full size: whoever put the face up framed the circle, and the
 * framing dialog told them the corners would not be shown. The passenger app opens
 * its people's faces with it, and the panel every account's.
 */
export function FaceViewer({ src, from, onClose }: { src: string; from: DOMRect; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const grip = useRef<{ y: number; moved: boolean } | null>(null);
  const closing = useRef(false);

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    window.setTimeout(onClose, FACE_RETURN_MS);
  }, [onClose]);
  useEscape(true, close);

  // Mounted where the circle is, and moved to the middle a frame later — it is the
  // change between the two that the browser animates.
  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setOpen(true));
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Edge to edge less a margin, on a phone held either way, but no bigger than a
  // hand-sized face: the photograph is a 512px square, and past this it only shows its
  // pixels.
  const side = Math.min(Math.min(window.innerWidth, window.innerHeight) - 48, 420);
  const shift = drag ?? 0;
  const rest = {
    left: (window.innerWidth - side) / 2,
    top: (window.innerHeight - side) / 2 + shift,
    width: side,
    height: side,
  };
  const at = open ? rest : { left: from.left, top: from.top, width: from.width, height: from.height };
  // Half a screen's drag takes the dark away entirely.
  const away = Math.min(Math.abs(shift) / (window.innerHeight / 2), 1);
  const ms = open ? FACE_FLIGHT_MS : FACE_RETURN_MS;

  return (
    <div
      className="face-viewer"
      role="dialog"
      aria-label="Фото"
      onClick={(event) => {
        // React carries a portal's clicks up to the card the circle sits in.
        event.stopPropagation();
        if (!grip.current?.moved) close();
        grip.current = null;
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        grip.current = { y: event.clientY, moved: false };
        setDrag(0);
      }}
      onPointerMove={(event) => {
        const held = grip.current;
        if (!held || drag === null) return;
        const dy = event.clientY - held.y;
        if (Math.abs(dy) > 6) held.moved = true;
        setDrag(dy);
      }}
      onPointerUp={() => {
        if (Math.abs(shift) > 96) close();
        else setDrag(null);
      }}
      onPointerCancel={() => setDrag(null)}
    >
      <div
        className="face-viewer-ground"
        style={{ opacity: open ? 0.9 * (1 - away) : 0, transitionDuration: `${ms}ms` }}
      />
      <img
        src={src}
        alt=""
        style={{
          ...at,
          // Under the finger it follows at once; let go, it flies. A zero duration
          // rather than `transition: none`, which React will not mix with the longhand.
          transitionDuration: drag !== null && open ? '0ms' : `${ms}ms`,
        }}
      />
    </div>
  );
}
